import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";

const FAQItem = ({ faq, isOpen, toggleOpen }) => {
  const { t } = useTranslation();

  return (
    <div className="border-b border-slate-200 dark:border-neutral-800 py-4">
      <button
        onClick={toggleOpen}
        className="w-full flex items-center justify-between text-left py-2 focus:outline-none group"
      >
        <span className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-purple-400 transition-colors">
          {t(faq.qKey, faq.defaultQ)}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 p-1"
        >
          <FiChevronDown className="w-5 h-5" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="py-2 text-sm text-slate-500 dark:text-neutral-400 leading-relaxed max-w-3xl">
              {t(faq.aKey, faq.defaultA)}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      qKey: "faq_timeline_q",
      defaultQ: "What is your typical project timeline?",
      aKey: "faq_timeline_a",
      defaultA: "Timelines depend entirely on project scope. A standard mobile or web application MVP usually takes 6 to 12 weeks from discovery to deployment. Large enterprise system customizing or ERP setups may take 3 to 6 months.",
    },
    {
      qKey: "faq_tech_q",
      defaultQ: "What technologies do you specialize in?",
      aKey: "faq_tech_a",
      defaultA: "We specialize in Flutter for mobile app development, React/Next.js and Node.js/Express for full-stack web applications, Firebase/MongoDB/PostgreSQL for databases, Docker/AWS for cloud operations, and customized SAP integration services.",
    },
    {
      qKey: "faq_maintenance_q",
      defaultQ: "Do you offer post-launch support and maintenance?",
      aKey: "faq_maintenance_a",
      defaultA: "Yes, we offer flexible support SLAs. This includes ongoing bug fixes, system version updates, performance tuning, and new feature integrations. We support you through long-term partner development.",
    },
    {
      qKey: "faq_comm_q",
      defaultQ: "How do you handle project communication and updates?",
      aKey: "faq_comm_a",
      defaultA: "We set up weekly demo calls, work in bi-weekly Agile sprints, and share project progress via Slack/Teams. You will have full access to staging build links to preview developments in real time.",
    },
    {
      qKey: "faq_pricing_q",
      defaultQ: "How do you structure project pricing?",
      aKey: "faq_pricing_a",
      defaultA: "We work with two models: Fixed-price agreements for well-defined specifications, and Time & Materials (hourly/sprint contracts) for scaling software projects with evolving requirement scopes.",
    },
  ];

  const handleToggle = (idx) => {
    setOpenIndex((prevIdx) => (prevIdx === idx ? null : idx));
  };

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative">
      {/* Background gradients */}
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-blue-500/5 dark:bg-purple-500/5 blur-3xl" />

      <div className="max-w-4xl mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('faqLabel', 'Got Questions?')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('faqTitle', 'Frequently Asked Questions')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
        </div>

        {/* FAQs list */}
        <div className="flex flex-col gap-2 max-w-3xl mx-auto border-t border-slate-200 dark:border-neutral-800 pt-4">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              toggleOpen={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
